import { logger, setLoggerTransportErrorHandler } from '@/services/logger/logger';

type ErrorReporter = {
  captureException?: (error: unknown, context?: unknown) => void;
  captureMessage?: (message: string, level?: string) => void;
};

let errorReporter: ErrorReporter | null = null;

/**
 * Зарегистрировать внешний репортёр ошибок (например, Sentry).
 * Весь остальной код не знает, что именно используется под капотом.
 */
export function setErrorReporter(reporter: ErrorReporter | null): void {
  errorReporter = reporter;

  // Подвязываем репортёр к ошибкам транспорта логгера (неудачные запросы на /api/logs).
  // Здесь важно не вызывать logger внутри обработчика, чтобы не зациклиться.
  setLoggerTransportErrorHandler(
    reporter?.captureException
      ? (error, info) => {
          reporter.captureException?.(error, { extra: { loggerTransport: info } });
        }
      : null,
  );
}

/** Отправить ошибку во внешний репортёр, если он инициализирован. */
export function reportError(error: unknown, context?: unknown): void {
  if (!errorReporter?.captureException) return;
  errorReporter.captureException(error, context);
}

function onError(event: ErrorEvent): void {
  const message = event.message ?? 'Unknown error';
  const error = event.error != null ? event.error : new Error(message);

  const context = { filename: event.filename, lineno: event.lineno, colno: event.colno };

  logger.error('Uncaught error', context, error instanceof Error ? error : undefined);
  reportError(error, { extra: context });
}

function onUnhandledRejection(event: PromiseRejectionEvent): void {
  const reason = event.reason;
  const error = reason instanceof Error ? reason : new Error(String(reason));

  logger.error('Unhandled promise rejection', {}, error);
  reportError(error, { extra: { reason } });
}

export function installGlobalErrorHandlers(): void {
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
}

export function uninstallGlobalErrorHandlers(): void {
  window.removeEventListener('error', onError);
  window.removeEventListener('unhandledrejection', onUnhandledRejection);
}
